import React, { useEffect, useRef, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import MemoryBubble3D from './MemoryBubble3D';
import '../MemoryGraph.css';

const LABEL_COLORS = {
  // Memory categories (warm earth-tone palette)
  'experiences': '#3b725f',
  'preferences': '#7f5f43',
  'relationships': '#bd7b69',
  'memories': '#cccac5',
  'thoughts': '#D8BFD8',
  'ambient_capture': '#2D9CDB',
  // New label categories
  'Work': '#FF6B6B',
  'Family': '#4ECDC4',
  'Personal': '#45B7D1',
  'Private': '#96CEB4',
  'Health': '#FECA57',
  'Finance': '#48DBFB',
  'Education': '#FF9FF3',
  'Hobby': '#54A0FF',
  'Travel': '#FD79A8',
  'Social': '#A29BFE',
  'Goals': '#6C5CE7',
  'Ideas': '#FFC048'
};

const MemoryGraph = forwardRef(function MemoryGraph({ memories, onMemoryClick, selectedMemory, highlightedMemoryIds = [], isSpeaking = false, compact = false }, ref) {
  const graphRef = useRef();
  const bubble3dRef = useRef();
  const wrapperRef = useRef(null);
  const imageCacheRef = useRef({}); // { url: HTMLImageElement }
  // Tracks the `compact` value we last did an initial fit-to-frame for, so we
  // fit exactly once per mount (and once again if compact toggles) without
  // re-firing on every subsequent graphData change.
  const didFitForCompactRef = useRef(null);
  const videoCacheRef = useRef({}); // { url: HTMLVideoElement }
  const [hoveredNode, setHoveredNode] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [graphStats, setGraphStats] = useState({ nodes: 0, edges: 0, clusters: 0, people: 0 });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [connectionType, setConnectionType] = useState('all'); // 'all', 'categories', 'people'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Safe wrappers for graph interactions (avoid null ref errors).
  // In compact mode we render MemoryBubble3D (Three.js), which exposes its own
  // zoom API via bubble3dRef; in full-graph mode the 2D ForceGraph handles zoom.
  const safeZoomToFit = useCallback(() => {
    if (compact && bubble3dRef.current) {
      bubble3dRef.current.zoomToFit();
    } else if (graphRef.current && typeof graphRef.current.zoomToFit === 'function') {
      graphRef.current.zoomToFit(400, 50);
    }
  }, [compact]);

  const safeZoomIn = useCallback(() => {
    if (compact && bubble3dRef.current) {
      bubble3dRef.current.zoomIn();
    } else if (graphRef.current && typeof graphRef.current.zoom === 'function') {
      graphRef.current.zoom(1.2);
    }
  }, [compact]);

  const safeZoomOut = useCallback(() => {
    if (compact && bubble3dRef.current) {
      bubble3dRef.current.zoomOut();
    } else if (graphRef.current && typeof graphRef.current.zoom === 'function') {
      graphRef.current.zoom(0.8);
    }
  }, [compact]);

  // Expose zoom methods to parent via ref
  useImperativeHandle(ref, () => ({
    zoomIn: safeZoomIn,
    zoomOut: safeZoomOut,
    zoomToFit: safeZoomToFit,
  }), [safeZoomIn, safeZoomOut, safeZoomToFit]);

  // Function to extract names from text
  const extractNamesFromText = (text) => {
    const names = new Set();
    
    // Pattern 1: Find capitalized words that are likely names (2+ characters)
    // Exclude common words that start with capital
    const commonWords = new Set(['The', 'This', 'That', 'When', 'Where', 'What', 'Who', 'Why', 'How', 
                                 'If', 'But', 'And', 'Or', 'In', 'On', 'At', 'To', 'From', 'By',
                                 'I', 'You', 'He', 'She', 'It', 'We', 'They', 'My', 'Your', 'His', 'Her']);
    
    // Pattern for finding potential names (Capitalized words)
    const namePattern = /\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g;
    const matches = text.match(namePattern) || [];
    
    matches.forEach(match => {
      // Filter out common words and very short matches
      const words = match.split(' ');
      if (!commonWords.has(words[0]) && match.length > 2) {
        // Check if it's at the start of a sentence (less likely to be a name)
        const index = text.indexOf(match);
        const beforeChar = index > 0 ? text[index - 1] : '';
        if (beforeChar !== '.' && beforeChar !== '!' && beforeChar !== '?') {
          names.add(match);
        }
      }
    });
    
    // Pattern 2: Find mentions with possessive (e.g., "John's", "Sarah's")
    const possessivePattern = /\b([A-Z][a-z]+)'s/g;
    let possessiveMatch;
    while ((possessiveMatch = possessivePattern.exec(text)) !== null) {
      if (!commonWords.has(possessiveMatch[1])) {
        names.add(possessiveMatch[1]);
      }
    }
    
    // Pattern 3: Find names after common relationship words
    const relationshipPattern = /(?:with|met|saw|told|asked|called|visited|helped|talked to|spoke with|mentioned)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/gi;
    let relMatch;
    while ((relMatch = relationshipPattern.exec(text)) !== null) {
      const name = relMatch[1];
      if (!commonWords.has(name.split(' ')[0])) {
        names.add(name);
      }
    }
    
    // Pattern 4: Family relationships with names
    const familyPattern = /(?:mom|dad|mother|father|sister|brother|aunt|uncle|cousin|grandmother|grandfather|friend)\s+([A-Z][a-z]+)/gi;
    let famMatch;
    while ((famMatch = familyPattern.exec(text)) !== null) {
      names.add(famMatch[1]);
    }
    
    return Array.from(names);
  };

  // Update dimensions on mount, window resize, and container resize (e.g. sidebar collapse)
  useEffect(() => {
    const updateDimensions = () => {
      const container = wrapperRef.current ? wrapperRef.current.querySelector('.memory-graph-container') : null;
      if (container) {
        const w = container.offsetWidth;
        const h = container.offsetHeight;
        if (w > 0 && h > 0) {
          setDimensions(prev => {
            if (prev.width !== w || prev.height !== h) {
              return { width: w, height: h };
            }
            return prev;
          });
        }
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    // Use ResizeObserver to detect container size changes (sidebar collapse/expand).
    // Note: we deliberately do NOT zoomToFit here. The compact-mode center force
    // gradually tightens the node cluster while the rotation animation runs, and
    // any resize-triggered zoomToFit would re-frame to the tightening cluster —
    // perceived as "the visual zooms in while spinning". Keeping zoom user-driven
    // (mousewheel / pinch / the zoom buttons) preserves whatever zoom level the
    // user set without the animation hijacking it.
    let resizeObserver = null;
    const container = wrapperRef.current ? wrapperRef.current.querySelector('.memory-graph-container') : null;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(container);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Recalculate dimensions when fullscreen toggles
  useEffect(() => {
    const timer = setTimeout(() => {
      const container = wrapperRef.current ? wrapperRef.current.querySelector('.memory-graph-container') : null;
      if (container) {
        setDimensions({ width: container.offsetWidth, height: container.offsetHeight });
      }
      safeZoomToFit();
    }, 50);
    return () => clearTimeout(timer);
  }, [isFullscreen, safeZoomToFit]);

  // Generate graph data from memories
  const graphData = useMemo(() => {
    const nodes = [];
    const links = [];
    const categoryNodes = new Map();
    const personNodes = new Map();
    const memoryToCategories = new Map();
    const memoryToPeople = new Map();

    // Extract people from all memories first
    memories.forEach(memory => {
      const peopleInMemory = extractNamesFromText(memory.text);
      memoryToPeople.set(memory.id, new Set(peopleInMemory));
      
      // Create person nodes
      peopleInMemory.forEach(person => {
        if (!personNodes.has(person)) {
          personNodes.set(person, {
            id: `person-${person}`,
            name: person,
            type: 'person',
            color: '#FFB6C1', // Light pink for people
            size: 12,
            memories: []
          });
        }
      });
    });

    // Create category/label nodes
    memories.forEach(memory => {
      const categories = new Set();
      
      // Add memory category
      if (memory.category && memory.category !== 'unknown') {
        categories.add(memory.category);
        if (!categoryNodes.has(memory.category)) {
          categoryNodes.set(memory.category, {
            id: `category-${memory.category}`,
            name: memory.category,
            type: 'category',
            color: LABEL_COLORS[memory.category] || '#3b725f',
            size: 15,
            memories: []
          });
        }
      }

      // Add memory labels
      const labels = Array.isArray(memory.labels) ? memory.labels : [];
      if (labels.length > 0) {
        labels.forEach(label => {
          categories.add(label);
          if (!categoryNodes.has(label)) {
            categoryNodes.set(label, {
              id: `category-${label}`,
              name: label,
              type: 'category',
              color: LABEL_COLORS[label] || '#666',
              size: 15,
              memories: []
            });
          }
        });
      }

      memoryToCategories.set(memory.id, categories);
    });

    // Bubble cloud colors per category
    const BUBBLE_COLORS = {
      'experiences': '#7FCDBB',
      'preferences': '#C49A6C',
      'relationships': '#E8A090',
      'memories': '#B0A8D0',
      'thoughts': '#D8BFD8',
      'goals': '#90C695',
      'ambient_capture': '#56CCF2',
      'uncategorized': '#C0BDB8',
    };

    // In compact/bubble mode: only memory nodes, no links, varying sizes
    if (compact) {
      memories.forEach((memory, idx) => {
        // Vary size based on text length and a seeded pseudo-random
        const textLen = memory.text ? memory.text.length : 50;
        const baseSize = 6 + Math.min(textLen / 30, 8);
        // Add variation using index to create visual variety
        const sizeVariation = Math.sin(idx * 2.7) * 3;
        const bubbleSize = Math.max(8, baseSize + sizeVariation);

        const catColor = BUBBLE_COLORS[memory.category] || BUBBLE_COLORS['uncategorized'];
        const hasMedia = !!(memory.media_url || memory.thumbnail_url);
        nodes.push({
          id: memory.id,
          name: memory.text.substring(0, 50) + (memory.text.length > 50 ? '...' : ''),
          fullText: memory.text,
          type: 'memory',
          category: memory.category,
          labels: memory.labels || [],
          timestamp: memory.timestamp,
          people: [],
          color: catColor,
          size: hasMedia ? Math.max(bubbleSize, 14) : bubbleSize,
          media_url: memory.media_url || null,
          media_type: memory.media_type || null,
          thumbnail_url: memory.thumbnail_url || null,
        });
      });

      setGraphStats({
        nodes: nodes.length,
        edges: 0,
        clusters: new Set(memories.map(m => m.category).filter(Boolean)).size,
        people: 0
      });

      return { nodes, links: [] };
    }

    // Create memory nodes and connect to categories and people
    memories.forEach(memory => {
      // Create memory node
      const hasMedia = !!(memory.media_url || memory.thumbnail_url);
      const memoryNode = {
        id: memory.id,
        name: memory.text.substring(0, 50) + (memory.text.length > 50 ? '...' : ''),
        fullText: memory.text,
        type: 'memory',
        category: memory.category,
        labels: memory.labels || [],
        timestamp: memory.timestamp,
        people: Array.from(memoryToPeople.get(memory.id) || []),
        color: '#E6E6FA', // Light purple for memory nodes
        size: hasMedia ? 14 : 8,
        media_url: memory.media_url || null,
        media_type: memory.media_type || null,
        thumbnail_url: memory.thumbnail_url || null,
      };
      nodes.push(memoryNode);

      // Create links to categories (if showing categories)
      if (connectionType === 'all' || connectionType === 'categories') {
        const categories = memoryToCategories.get(memory.id);
        categories.forEach(cat => {
          const categoryNode = categoryNodes.get(cat);
          if (categoryNode) {
            categoryNode.memories.push(memory.id);
            links.push({
              source: memory.id,
              target: categoryNode.id,
              type: 'category',
              value: 1,
              color: 'rgba(59, 114, 95, 0.2)'
            });
          }
        });
      }

      // Create links to people (if showing people)
      if (connectionType === 'all' || connectionType === 'people') {
        const people = memoryToPeople.get(memory.id);
        people.forEach(person => {
          const personNode = personNodes.get(person);
          if (personNode) {
            personNode.memories.push(memory.id);
            links.push({
              source: memory.id,
              target: personNode.id,
              type: 'person',
              value: 1.5,
              color: 'rgba(255, 182, 193, 0.4)' // Light pink for person connections
            });
          }
        });
      }
    });

    // Add category nodes to the graph
    if (connectionType === 'all' || connectionType === 'categories') {
      categoryNodes.forEach(catNode => {
        // Adjust size based on number of connected memories
        catNode.size = Math.min(25, 10 + catNode.memories.length * 2);
        nodes.push(catNode);
      });
    }

    // Add person nodes to the graph
    if (connectionType === 'all' || connectionType === 'people') {
      personNodes.forEach(personNode => {
        // Adjust size based on number of connected memories
        personNode.size = Math.min(20, 10 + personNode.memories.length * 3);
        nodes.push(personNode);
      });
    }

    // Create links between memories that share categories or people
    const processedPairs = new Set();
    memories.forEach((memory1, i) => {
      memories.forEach((memory2, j) => {
        if (i >= j) return; // Avoid duplicates and self-links
        
        const pairKey = `${memory1.id}-${memory2.id}`;
        if (processedPairs.has(pairKey)) return;
        processedPairs.add(pairKey);

        const cats1 = memoryToCategories.get(memory1.id);
        const cats2 = memoryToCategories.get(memory2.id);
        const people1 = memoryToPeople.get(memory1.id);
        const people2 = memoryToPeople.get(memory2.id);
        
        // Find shared categories
        const sharedCategories = new Set([...cats1].filter(x => cats2.has(x)));
        
        // Find shared people
        const sharedPeople = new Set([...people1].filter(x => people2.has(x)));
        
        // Create stronger links for memories that share people
        if (sharedPeople.size > 0 && (connectionType === 'all' || connectionType === 'people')) {
          links.push({
            source: memory1.id,
            target: memory2.id,
            type: 'shared-person',
            value: sharedPeople.size * 2, // Stronger connection for shared people
            color: 'rgba(255, 105, 180, 0.3)', // Hot pink for shared person connections
            sharedPeople: [...sharedPeople]
          });
        } else if (sharedCategories.size > 0 && (connectionType === 'all' || connectionType === 'categories')) {
          links.push({
            source: memory1.id,
            target: memory2.id,
            type: 'shared-category',
            value: sharedCategories.size,
            color: 'rgba(59, 114, 95, 0.1)',
            sharedCategories: [...sharedCategories]
          });
        }
      });
    });

    // Calculate statistics
    const clusters = new Set();
    const peopleCount = new Set();
    
    categoryNodes.forEach(cat => {
      if (cat.memories.length > 0) clusters.add(cat.name);
    });
    
    personNodes.forEach(person => {
      if (person.memories.length > 0) peopleCount.add(person.name);
    });
    
    setGraphStats({
      nodes: nodes.length,
      edges: links.length,
      clusters: clusters.size,
      people: peopleCount.size
    });

    return { nodes, links };
  }, [memories, connectionType, compact]);

  // Handle node click
  const handleNodeClick = (node) => {
    if (node.type === 'memory' && onMemoryClick) {
      onMemoryClick(memories.find(m => m.id === node.id));
    } else if (node.type === 'category') {
      setSelectedCategory(node.name === selectedCategory ? null : node.name);
      setSelectedPerson(null);
    } else if (node.type === 'person') {
      setSelectedPerson(node.name === selectedPerson ? null : node.name);
      setSelectedCategory(null);
    }
  };

  // Pre-load images for nodes that have media
  useEffect(() => {
    const cache = imageCacheRef.current;
    graphData.nodes.forEach(node => {
      const imgUrl = node.thumbnail_url || node.media_url;
      if (imgUrl && node.media_type === 'image' && !cache[imgUrl]) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imgUrl;
        cache[imgUrl] = { img, loaded: false };
        img.onload = () => { cache[imgUrl].loaded = true; };
        img.onerror = () => { cache[imgUrl].loaded = false; };
      }
    });
  }, [graphData]);

  // Pre-load videos for nodes that have video media
  const hasVideoNodes = useMemo(() => graphData.nodes.some(n => n.media_type === 'video' && n.media_url), [graphData]);

  useEffect(() => {
    const cache = videoCacheRef.current;
    graphData.nodes.forEach(node => {
      if (node.media_type === 'video' && node.media_url && !cache[node.media_url]) {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.src = node.media_url;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.autoplay = true;
        video.preload = 'auto';
        video.style.display = 'none';
        document.body.appendChild(video);
        cache[node.media_url] = { video, ready: false };
        video.addEventListener('canplaythrough', () => {
          cache[node.media_url].ready = true;
          video.play().catch(() => {});
        });
        video.addEventListener('error', () => { cache[node.media_url].ready = false; });
        video.load();
      }
    });
    // Cleanup: pause and remove hidden video elements on unmount
    return () => {
      Object.values(cache).forEach(entry => {
        try {
          entry.video.pause();
          entry.video.removeAttribute('src');
          if (entry.video.parentNode) entry.video.parentNode.removeChild(entry.video);
        } catch (e) { /* ignore */ }
      });
      videoCacheRef.current = {};
    };
  }, [graphData]);

  // Throttled redraw loop when video nodes are present so video frames update (~15fps)
  useEffect(() => {
    if (!hasVideoNodes) return;
    let intervalId;
    intervalId = setInterval(() => {
      if (graphRef.current) {
        try { graphRef.current.d3ReheatSimulation(); } catch (e) { /* ignore */ }
      }
    }, 66); // ~15fps — smooth enough for video, gentle on the graph physics
    return () => clearInterval(intervalId);
  }, [hasVideoNodes]);

  // Compute available categories with counts and colors for filter pills
  const categoryCounts = useMemo(() => {
    const CATEGORY_COLORS = {
      'experiences': '#7FCDBB',
      'preferences': '#C49A6C',
      'relationships': '#E8A090',
      'memories': '#B0A8D0',
      'thoughts': '#D8BFD8',
      'goals': '#90C695',
      'ambient_capture': '#56CCF2',
      'uncategorized': '#C0BDB8',
    };
    const counts = {};
    memories.forEach(m => {
      const cat = m.category || 'uncategorized';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([cat, count]) => ({
        name: cat,
        count,
        color: CATEGORY_COLORS[cat] || LABEL_COLORS[cat] || '#888',
      }));
  }, [memories]);

  // Compute matching memory IDs for search
  const searchMatchIds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null; // null = no search active
    const ids = new Set();
    memories.forEach(mem => {
      const text = (mem.text || '').toLowerCase();
      const cat = (mem.category || '').toLowerCase();
      const lbls = Array.isArray(mem.labels) ? mem.labels.join(' ').toLowerCase() : '';
      if (text.includes(q) || cat.includes(q) || lbls.includes(q)) {
        ids.add(mem.id);
      }
    });
    return ids;
  }, [searchQuery, memories]);

  // Custom node rendering
  const nodeCanvasObject = (node, ctx, globalScale) => {
    const label = node.name;
    const fontSize = node.type === 'category' || node.type === 'person' ? 14 / globalScale : 12 / globalScale;
    ctx.font = `${fontSize}px Sans-Serif`;
    
    // Draw node
    ctx.beginPath();
    
    // Different shapes for different node types
    if (node.type === 'person') {
      // Diamond shape for person nodes
      const size = node.size;
      ctx.moveTo(node.x, node.y - size);
      ctx.lineTo(node.x + size, node.y);
      ctx.lineTo(node.x, node.y + size);
      ctx.lineTo(node.x - size, node.y);
      ctx.closePath();
    } else {
      // Circle for other nodes
      ctx.arc(node.x, node.y, node.size, 0, 2 * Math.PI, false);
    }
    
    // Highlight selected, hovered, or AI-referenced nodes
    const isHighlightedByAI = highlightedMemoryIds.length > 0 && node.type === 'memory' && highlightedMemoryIds.some(hm => hm.id === node.id);
    if (isHighlightedByAI && isSpeaking) {
      // Dramatic speaking animation — memories "talk back"
      const t = Date.now();
      const breathPhase = (t % 800) / 800; // Faster 0.8s cycle
      const breath = Math.sin(breathPhase * Math.PI * 2);
      const ripplePhase = (t % 1500) / 1500;

      // Expanding ripple rings
      for (let ring = 2; ring >= 0; ring--) {
        const ringPhase = (ripplePhase + ring * 0.33) % 1;
        const ringRadius = node.size + ringPhase * 18;
        const ringAlpha = (1 - ringPhase) * 0.25;
        ctx.beginPath();
        ctx.arc(node.x, node.y, ringRadius, 0, 2 * Math.PI, false);
        ctx.strokeStyle = `rgba(74, 138, 115, ${ringAlpha})`;
        ctx.lineWidth = (2 - ringPhase * 1.5) / globalScale;
        ctx.stroke();
      }

      // Re-draw the node circle on top of ripples
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size + breath * 2, 0, 2 * Math.PI, false);
      ctx.shadowColor = '#4a8a73';
      ctx.shadowBlur = 20 + breath * 12;
      ctx.fillStyle = `rgba(74, 138, 115, ${0.85 + breath * 0.1})`;
      ctx.strokeStyle = '#3b725f';
      ctx.lineWidth = (3 + breath) / globalScale;
    } else if (isHighlightedByAI) {
      // Gentle pulsing glow for AI-referenced memories (not speaking)
      const pulsePhase = (Date.now() % 2000) / 2000;
      const glowSize = 3 + Math.sin(pulsePhase * Math.PI * 2) * 2;
      ctx.shadowColor = '#3b725f';
      ctx.shadowBlur = 15 + Math.sin(pulsePhase * Math.PI * 2) * 8;
      ctx.fillStyle = '#4a8a73';
      ctx.strokeStyle = '#3b725f';
      ctx.lineWidth = glowSize / globalScale;
    } else if (hoveredNode === node.id || (selectedMemory && selectedMemory.id === node.id)) {
      ctx.fillStyle = node.color;
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 3 / globalScale;
    } else if (searchMatchIds && node.type === 'memory') {
      // Search-based highlighting
      if (searchMatchIds.has(node.id)) {
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 12;
        ctx.fillStyle = node.color;
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 2.5 / globalScale;
      } else {
        ctx.fillStyle = 'rgba(230, 230, 250, 0.1)';
        ctx.strokeStyle = 'rgba(59, 114, 95, 0.08)';
        ctx.lineWidth = 0.5 / globalScale;
      }
    } else if (searchMatchIds && (node.type === 'category' || node.type === 'person')) {
      // Dim category/person nodes during search
      ctx.fillStyle = 'rgba(230, 230, 250, 0.15)';
      ctx.strokeStyle = 'rgba(59, 114, 95, 0.1)';
      ctx.lineWidth = 0.5 / globalScale;
    } else if (highlightedMemoryIds.length > 0 && node.type === 'memory') {
      // Dim non-highlighted memories when AI references are active
      ctx.fillStyle = 'rgba(230, 230, 250, 0.15)';
      ctx.strokeStyle = 'rgba(59, 114, 95, 0.1)';
      ctx.lineWidth = 1 / globalScale;
    } else if ((selectedCategory && node.type === 'memory') || (selectedPerson && node.type === 'memory')) {
      // Highlight memories in selected category or connected to selected person
      const isInCategory = node.category === selectedCategory || 
                          (node.labels && node.labels.includes(selectedCategory));
      const isConnectedToPerson = node.people && node.people.includes(selectedPerson);
      const isHighlighted = isInCategory || isConnectedToPerson;
      
      ctx.fillStyle = isHighlighted ? node.color : 'rgba(230, 230, 250, 0.3)';
      ctx.strokeStyle = isHighlighted ? 
        (isConnectedToPerson ? '#FFB6C1' : node.color) : 
        'rgba(59, 114, 95, 0.2)';
      ctx.lineWidth = isHighlighted ? 2 / globalScale : 1 / globalScale;
    } else {
      ctx.fillStyle = node.color;
      ctx.strokeStyle = node.type === 'category' ? node.color : 
                       node.type === 'person' ? '#FFB6C1' : 
                       'rgba(59, 114, 95, 0.3)';
      ctx.lineWidth = (node.type === 'category' || node.type === 'person') ? 2 / globalScale : 1 / globalScale;
    }
    
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';

    // Draw media image inside the bubble (clipped to circle)
    if (node.type === 'memory' && node.media_url) {
      const imgUrl = node.thumbnail_url || node.media_url;
      const cached = imageCacheRef.current[imgUrl];
      if (cached && cached.loaded && node.media_type === 'image') {
        ctx.save();
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size - 1, 0, 2 * Math.PI, false);
        ctx.clip();
        try {
          const r = node.size - 1;
          ctx.drawImage(cached.img, node.x - r, node.y - r, r * 2, r * 2);
        } catch (e) { /* ignore draw errors */ }
        ctx.restore();
        // Subtle border around image bubbles
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, 2 * Math.PI, false);
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 1.5 / globalScale;
        ctx.stroke();
      } else if (node.media_type === 'video') {
        // Draw live video frame inside the circle
        const videoEntry = videoCacheRef.current[node.media_url];
        if (videoEntry && videoEntry.ready && videoEntry.video.readyState >= 2) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size - 1, 0, 2 * Math.PI, false);
          ctx.clip();
          try {
            const r = node.size - 1;
            const vid = videoEntry.video;
            // Center-crop the video into the circle
            const vw = vid.videoWidth || vid.width || 1;
            const vh = vid.videoHeight || vid.height || 1;
            const aspect = vw / vh;
            let sx = 0, sy = 0, sw = vw, sh = vh;
            if (aspect > 1) { sx = (vw - vh) / 2; sw = vh; }
            else if (aspect < 1) { sy = (vh - vw) / 2; sh = vw; }
            ctx.drawImage(vid, sx, sy, sw, sh, node.x - r, node.y - r, r * 2, r * 2);
          } catch (e) { /* ignore draw errors */ }
          ctx.restore();
          // Subtle border around video bubbles
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size, 0, 2 * Math.PI, false);
          ctx.strokeStyle = 'rgba(255,255,255,0.5)';
          ctx.lineWidth = 1.5 / globalScale;
          ctx.stroke();
        } else {
          // Fallback: play icon while video is loading
          const triSize = node.size * 0.4;
          ctx.fillStyle = 'rgba(255,255,255,0.85)';
          ctx.beginPath();
          ctx.moveTo(node.x - triSize * 0.4, node.y - triSize * 0.5);
          ctx.lineTo(node.x - triSize * 0.4, node.y + triSize * 0.5);
          ctx.lineTo(node.x + triSize * 0.6, node.y);
          ctx.closePath();
          ctx.fill();
        }
      }
    }

    // Draw label
    const isSearchDimmed = searchMatchIds && node.type === 'memory' && !searchMatchIds.has(node.id);
    const isSearchDimmedOther = searchMatchIds && (node.type === 'category' || node.type === 'person');
    if (node.type === 'category' || node.type === 'person' || hoveredNode === node.id || globalScale > 0.5) {
      if (isSearchDimmed || isSearchDimmedOther) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
      } else {
        ctx.fillStyle = (node.type === 'category' || node.type === 'person') ? 'white' : 'rgba(255, 255, 255, 0.9)';
      }
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      if (node.type === 'category' || node.type === 'person') {
        // Category/Person label
        ctx.fillText(label, node.x, node.y);
        // Add icon for person nodes
        if (node.type === 'person') {
          ctx.font = `${10 / globalScale}px Sans-Serif`;
          ctx.fillText(`(${node.memories.length})`, node.x, node.y + node.size + 10 / globalScale);
        }
      } else if (hoveredNode === node.id) {
        // Show full text for hovered memory nodes
        const maxWidth = 200 / globalScale;
        const words = node.fullText.split(' ');
        const lines = [];
        let currentLine = '';
        
        words.forEach(word => {
          const testLine = currentLine + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word + ' ';
          } else {
            currentLine = testLine;
          }
        });
        lines.push(currentLine);
        
        // Draw background for text
        const lineHeight = fontSize * 1.2;
        const textHeight = lines.length * lineHeight;
        const textWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
        
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(
          node.x - textWidth / 2 - 5,
          node.y - textHeight / 2 - 5,
          textWidth + 10,
          textHeight + 10
        );
        
        // Draw text lines
        ctx.fillStyle = 'white';
        lines.forEach((line, i) => {
          ctx.fillText(
            line,
            node.x,
            node.y - (lines.length - 1) * lineHeight / 2 + i * lineHeight
          );
        });
      }
    }
  };

  // Animate pulsing glow for highlighted memories by reheating the simulation briefly
  useEffect(() => {
    if (highlightedMemoryIds.length > 0 && graphRef.current) {
      // Trigger a single short reheat so nodeCanvasObject redraws with glow
      try {
        graphRef.current.d3ReheatSimulation();
      } catch (e) {
        // Silently ignore if method unavailable
      }
    }
  }, [highlightedMemoryIds]);

  // Continuous canvas redraw while AI is speaking so animations stay smooth
  useEffect(() => {
    if (!isSpeaking || highlightedMemoryIds.length === 0) return;
    let rafId;
    const tick = () => {
      if (graphRef.current) {
        try { graphRef.current.d3ReheatSimulation(); } catch (e) { /* ignore */ }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isSpeaking, highlightedMemoryIds]);

  // Configure forces for bubble cloud in compact mode
  useEffect(() => {
    if (compact && graphRef.current) {
      // Remove link force (no connections in bubble mode)
      graphRef.current.d3Force('link', null);
      // Stronger charge to spread bubbles — creates spacing between them
      graphRef.current.d3Force('charge').strength(-30).distanceMax(250);
      // Weaker center force so they cluster together
      graphRef.current.d3Force('center').strength(0.12);
      // Reheat simulation
      graphRef.current.d3ReheatSimulation();
    }
  }, [compact, graphData]);

  // Gentle idle rotation in compact (bubble) mode
  useEffect(() => {
    if (!compact || !graphRef.current || memories.length === 0) return;
    // Pause rotation while AI is referencing memories
    if (highlightedMemoryIds.length > 0 && isSpeaking) {
      try { graphRef.current.d3Force('rotate', null); } catch(e) {}
      return;
    }
    const ROTATION_SPEED = 0.0003;
    function createRotationForce() {
      let nodes = [];
      function force() {
        if (nodes.length < 2) return;
        let cx = 0, cy = 0;
        nodes.forEach(n => { cx += (n.x || 0); cy += (n.y || 0); });
        cx /= nodes.length;
        cy /= nodes.length;
        nodes.forEach(n => {
          const dx = (n.x || 0) - cx;
          const dy = (n.y || 0) - cy;
          n.vx = (n.vx || 0) + (-dy * ROTATION_SPEED);
          n.vy = (n.vy || 0) + (dx * ROTATION_SPEED);
        });
      }
      force.initialize = (_nodes) => { nodes = _nodes; };
      return force;
    }
    graphRef.current.d3Force('rotate', createRotationForce());
    return () => {
      if (graphRef.current) {
        try { graphRef.current.d3Force('rotate', null); } catch(e) {}
      }
    };
  }, [compact, graphData, memories, highlightedMemoryIds, isSpeaking]);

  // Initial fit-to-frame so the user can see all memories. Fires exactly once
  // per (mode, populated graph) — so the user gets a sensible starting view,
  // but subsequent graphData changes (new memories, highlights) don't keep
  // re-zooming as the spin runs. Compact mode uses larger padding to leave
  // breathing room around the bubble cluster instead of cramming it edge-to-edge.
  useEffect(() => {
    if (!graphRef.current) return;
    if (!graphData || !graphData.nodes || graphData.nodes.length === 0) return;
    if (didFitForCompactRef.current === compact) return;

    const timer = setTimeout(() => {
      if (!graphRef.current || typeof graphRef.current.zoomToFit !== 'function') return;
      const padding = compact ? 100 : 50;
      graphRef.current.zoomToFit(400, padding);
      didFitForCompactRef.current = compact;
    }, 600);

    return () => clearTimeout(timer);
  }, [graphData, compact]);

  return (
    <div className={`memory-graph-wrapper ${isFullscreen ? 'fullscreen' : ''}`} ref={wrapperRef}>
      {/* Top bar: total count + dropdown filter */}
      <div className="mg-top-bar">
        <div className="mg-total-count">
          <span className="mg-total-value">{memories.length}</span>
          <span className="mg-total-label">memories</span>
        </div>
        <div className="mg-filter-dropdown-wrap">
          <button
            className={`mg-filter-btn ${selectedCategory ? 'has-filter' : ''}`}
            onClick={() => setShowCategoryDropdown(prev => !prev)}
          >
            {selectedCategory ? (
              <>
                <span className="mg-cat-dot" style={{ background: categoryCounts.find(c => c.name === selectedCategory)?.color || '#888' }} />
                {selectedCategory}
                <span
                  className="mg-filter-clear"
                  onClick={(e) => { e.stopPropagation(); setSelectedCategory(null); setShowCategoryDropdown(false); }}
                >×</span>
              </>
            ) : (
              <>Filter ▾</>
            )}
          </button>
          {showCategoryDropdown && (
            <div className="mg-filter-dropdown">
              <button
                className={`mg-filter-option ${selectedCategory === null ? 'active' : ''}`}
                onClick={() => { setSelectedCategory(null); setShowCategoryDropdown(false); }}
              >
                All categories
              </button>
              {categoryCounts.map(cat => (
                <button
                  key={cat.name}
                  className={`mg-filter-option ${selectedCategory === cat.name ? 'active' : ''}`}
                  onClick={() => { setSelectedCategory(cat.name); setShowCategoryDropdown(false); }}
                >
                  <span className="mg-cat-dot" style={{ background: cat.color }} />
                  <span className="mg-filter-option-name">{cat.name}</span>
                  <span className="mg-filter-option-count">{cat.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mg-search-bar">
        <input
          type="text"
          className="mg-search-input"
          placeholder="Search memories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <span className="mg-search-info">
            {searchMatchIds ? searchMatchIds.size : 0} match{searchMatchIds && searchMatchIds.size !== 1 ? 'es' : ''}
            <button className="mg-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
          </span>
        )}
      </div>

      <div className="memory-graph-container">
        {compact ? (
          <MemoryBubble3D
            ref={bubble3dRef}
            nodes={graphData.nodes}
            onMemoryClick={(node) => handleNodeClick(node)}
            selectedMemory={selectedMemory}
            highlightedMemoryIds={highlightedMemoryIds}
            isSpeaking={isSpeaking}
            searchMatchIds={searchMatchIds}
          />
        ) : (
          <ForceGraph2D
            ref={graphRef}
            graphData={graphData}
            width={dimensions.width}
            height={dimensions.height}
            nodeLabel={node => {
              if (node.type === 'category') return `${node.name} (${node.memories.length} memories)`;
              if (node.type === 'person') return `${node.name} (mentioned in ${node.memories.length} memories)`;
              return node.name;
            }}
            nodeColor={node => node.color}
            nodeRelSize={1}
            nodeCanvasObject={nodeCanvasObject}
            nodeCanvasObjectMode={() => 'replace'}
            linkColor={link => link.color}
            linkWidth={link => Math.sqrt(link.value)}
            linkOpacity={0.3}
            linkDirectionalParticles={0}
            linkVisibility={true}
            onNodeClick={handleNodeClick}
            onNodeHover={node => setHoveredNode(node ? node.id : null)}
            nodePointerAreaPaint={(node, color, ctx) => {
              const hitRadius = Math.max(node.size + 4, 12);
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, hitRadius, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
            enableZoomPanInteraction={true}
            enableNodeDrag={true}
            cooldownTicks={100}
            cooldownTime={15000}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
          />
        )}
      </div>

      {selectedMemory && (
        <div className="selected-memory-info">
          <h4>Selected Memory</h4>
          <p>{selectedMemory.text}</p>
          <div className="memory-meta">
            <span className="category-badge" style={{ backgroundColor: LABEL_COLORS[selectedMemory.category] || '#3b725f' }}>
              {selectedMemory.category}
            </span>
            <span className="date">{new Date(selectedMemory.timestamp).toLocaleDateString()}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default MemoryGraph;
